// legalVersions — bump-on-change tracker for /privacy and /terms.
//
// When we update the legal copy, we bump the version constant. On
// next login the app compares the user's last-accepted version to
// the current one; if different, we show a "Accept new terms" modal
// before letting them continue.
//
// V1 stores acceptance in localStorage keyed by user id, which means
// a fresh device prompts again. That's acceptable for MVP; an
// `legal_acceptance` table can be added later if we need server-side
// truth.

/** Current published version of /privacy. Bump when copy changes. */
export const PRIVACY_VERSION = '2026-06-06';

/** Current published version of /terms. Bump when copy changes. */
export const TERMS_VERSION = '2026-06-06';

/** Current published version of /legal/security. Bump when copy changes.
 *  Inte en del av acceptance-trackern (security är informativ, inte ett
 *  avtal), men exporteras här så Security.tsx kan synka datumet automatiskt
 *  vid bump. (#51) */
export const SECURITY_VERSION = '2026-06-06';

const KEY_PREFIX = 'fw.legal.accept.';

interface AcceptanceRecord {
  privacyVersion: string;
  termsVersion: string;
  acceptedAt: string;
}

function key(userId: string): string {
  return KEY_PREFIX + userId;
}

export function getAcceptance(userId: string): AcceptanceRecord | null {
  try {
    const raw = localStorage.getItem(key(userId));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as AcceptanceRecord;
    if (!parsed.privacyVersion || !parsed.termsVersion) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function recordAcceptance(userId: string): AcceptanceRecord {
  const rec: AcceptanceRecord = {
    privacyVersion: PRIVACY_VERSION,
    termsVersion: TERMS_VERSION,
    acceptedAt: new Date().toISOString(),
  };
  try {
    localStorage.setItem(key(userId), JSON.stringify(rec));
  } catch {
    /* ignore */
  }
  return rec;
}

export function needsReAccept(userId: string): boolean {
  const rec = getAcceptance(userId);
  if (!rec) return true;
  return rec.privacyVersion !== PRIVACY_VERSION
    || rec.termsVersion !== TERMS_VERSION;
}

export const LEGAL_VERSIONS = {
  privacy: PRIVACY_VERSION,
  terms: TERMS_VERSION,
} as const;
